import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Box, Autocomplete, TextField,
  Chip, Stack
} from "@mui/material";
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import ClearAllIcon from '@mui/icons-material/ClearAll';

const COMMON_PANTRY_STAPLES = [
  "Chicken Breast", "Eggs", "Garlic", "Onion", "Butter",
  "Olive Oil", "Rice", "Pasta", "Cheddar Cheese", "Milk",
  "Potatoes", "Tomatoes", "Ground Beef", "Bacon"
];

const PantryMatcherDialog = ({
  open,
  onClose,
  pantryItems,
  onPantryItemsChange,
  availableIngredients = []
}) => {
  const handleToggleStaple = (staple) => {
    if (pantryItems.includes(staple)) {
      onPantryItemsChange(pantryItems.filter(item => item !== staple));
    } else {
      onPantryItemsChange([...pantryItems, staple]);
    }
  };

  const handleClear = () => {
    onPantryItemsChange([]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#D28415' }}>
        <SoupKitchenIcon />
        <Typography variant="h6" component="span" sx={{ fontFamily: "'Patrick Hand', cursive", fontSize: '1.4rem' }}>
          What Can I Make? (Pantry Matcher)
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select the ingredients you currently have in your kitchen or fridge. We'll find recipes that make the best use of what you have!
        </Typography>

        {/* Multi-Select Autocomplete */}
        <Autocomplete
          multiple
          freeSolo
          options={availableIngredients}
          value={pantryItems}
          onChange={(event, newValue) => onPantryItemsChange(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                color="primary"
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Your available ingredients"
              placeholder="Search or add ingredient..."
              helperText="Press Enter after typing to add custom items"
            />
          )}
        />

        {/* Quick Pantry Staples */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 1 }}>
            QUICK TAP COMMON STAPLES:
          </Typography>
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {COMMON_PANTRY_STAPLES.map((staple) => {
              const isSelected = pantryItems.includes(staple);
              return (
                <Chip
                  key={staple}
                  label={staple}
                  size="small"
                  onClick={() => handleToggleStaple(staple)}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClear}
          color="inherit"
          startIcon={<ClearAllIcon />}
          disabled={pantryItems.length === 0}
        >
          Clear All
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ bgcolor: '#D28415', '&:hover': { bgcolor: '#b36b10' }, fontWeight: 'bold' }}
        >
          {pantryItems.length > 0 ? `Apply (${pantryItems.length} items)` : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PantryMatcherDialog;
